/**
 * 共有パネル（FR-LINK-03〜05）。
 *
 * 中身は上から: 共有 URL（コピー）／QR／共有先リンク／埋め込みコード（コピー）。
 * パネル方式にしているのは、QR（正方形）・埋め込みコード（複数行）・リンク列が
 * ツールバーのポップオーバーに収まらないため。
 *
 * URL は location ではなく `getState().shareUrl` から取る。ズーム・パン後の
 * URL 書き戻しは間引かれるが、状態は即時に反映されるので常に最新になる。
 */
import { cls } from '../brand';
import type { DisposeRegistry } from '../core/dispose-registry';
import type { ShareOptions } from '../core/options';
import type { ViewerState } from '../core/viewer';
import type { Translate } from '../i18n/i18n';
import type { ViewerApi } from '../ui/api';
import type { PanelContent } from '../ui/panels';
import { buildEmbedCode } from './embed-code';
import { encodeQr } from './qr';
import { qrSvg } from './qr-svg';
import { safeTargetUrl } from './share-targets';

/** URL からハッシュ（#…）を落とす。 */
function stripHash(url: string): string {
  const index = url.indexOf('#');
  return index < 0 ? url : url.slice(0, index);
}

export class SharePanel implements PanelContent {
  readonly el: HTMLElement;

  private readonly urlInput: HTMLInputElement;
  private readonly embedInput: HTMLTextAreaElement;
  private readonly qrBox: HTMLDivElement;
  private readonly targetList: HTMLUListElement;
  /** 最後に描いた URL（同じなら QR を焼き直さない）。 */
  private rendered = '';

  constructor(
    private readonly doc: Document,
    private readonly registry: DisposeRegistry,
    private readonly api: ViewerApi,
    private readonly translate: Translate,
    private readonly options: ShareOptions,
    private readonly announce: (message: string) => void,
  ) {
    this.el = doc.createElement('div');
    this.el.className = cls('share');

    this.urlInput = this.field('input', 'share-url', translate('share.url')) as HTMLInputElement;
    this.urlInput.type = 'text';
    this.el.append(this.section('share.url', this.urlInput, 'share-copy-url'));

    this.qrBox = doc.createElement('div');
    this.qrBox.className = cls('share-qr');
    if (options.qr) this.el.appendChild(this.qrBox);

    this.targetList = doc.createElement('ul');
    this.targetList.className = cls('share-targets');
    // 共有先が設定されていなければ節ごと出さない（既定セットに依存しない・FR-LINK-05）
    if (options.targets.length > 0) this.el.appendChild(this.targetList);

    this.embedInput = this.field(
      'textarea',
      'share-embed',
      translate('share.embed'),
    ) as HTMLTextAreaElement;
    this.embedInput.rows = 3;
    this.el.append(this.section('share.embed', this.embedInput, 'share-copy-embed'));
  }

  /** パネルを開いたら最新化し、URL を選択して即コピーできる状態にする。 */
  activate(): void {
    this.sync();
    this.urlInput.select();
  }

  /** 状態変化のたびに Shell から配られる（開いているときだけ）。 */
  sync(state: ViewerState = this.api.getState()): void {
    const url = state.shareUrl;
    if (url === this.rendered) return;
    this.rendered = url;

    this.urlInput.value = url;
    // 埋め込み既定先は共有 URL（正規 OGP ページ・M5b-2）のハッシュを外したもの。
    // embedBase の明示指定があればそちらが勝つ。埋め込みはズーム位置を持たず page のみ。
    const embedBase = this.options.embedBase || stripHash(url);
    this.embedInput.value = buildEmbedCode(
      this.doc.defaultView as Window,
      '',
      { page: state.page },
      {
        base: embedBase,
        allowlist: this.options.embedAllowlist,
        width: this.options.embedWidth,
        height: this.options.embedHeight,
      },
    );
    if (this.options.qr) this.renderQr(url);
    if (this.options.targets.length > 0) this.renderTargets(url, state.label);
  }

  dispose(): void {
    this.el.remove();
  }

  // -- 内部 ----------------------------------------------------------------

  private renderQr(url: string): void {
    const matrix = encodeQr(url);
    if (!matrix) {
      // 容量を超えた URL では QR を出さず、理由を文言で示す
      this.qrBox.textContent = this.translate('share.qrTooLong');
      return;
    }
    // innerHTML だが注入経路は無い: qrSvg が組む文字列は行列（0/1）から導いた
    // 数値と、属性エスケープ済みのラベルだけで、共有 URL 自体は一切埋め込まれない
    // （URL は行列に符号化される）。アイコン（ui/icons.ts）と同じ扱い。
    this.qrBox.innerHTML = qrSvg(matrix, this.translate('share.qrLabel'));
  }

  private renderTargets(url: string, label: string): void {
    const title = this.translate('share.linkTitle', { page: label });
    const fragment = this.doc.createDocumentFragment();
    for (const target of this.options.targets) {
      // http/https/mailto/tel 以外の共有先は描かない（`javascript:` の設定ミス・混入対策）
      const href = safeTargetUrl(target, url, title, this.doc.baseURI);
      if (href === null) continue;

      const li = this.doc.createElement('li');
      const link = this.doc.createElement('a');
      link.className = cls('share-target');
      link.href = href;
      link.target = '_blank';
      link.rel = 'noopener noreferrer'; // NFR-SEC-02
      link.textContent = target.label;
      li.appendChild(link);
      fragment.appendChild(li);
    }
    this.targetList.replaceChildren(fragment);
  }

  private field(tag: 'input' | 'textarea', className: string, label: string): HTMLElement {
    const el = this.doc.createElement(tag);
    el.className = cls(className);
    el.readOnly = true;
    // name の無い欄は DevTools が指摘する（id は複数ビューワで衝突するので使わない）。
    el.name = className;
    el.setAttribute('aria-label', label);
    return el;
  }

  /** ラベル・入力欄・コピーボタンの 1 組。 */
  private section(
    labelKey: string,
    field: HTMLElement,
    buttonClass: string,
  ): HTMLDivElement {
    const wrap = this.doc.createElement('div');
    wrap.className = cls('share-section');

    const heading = this.doc.createElement('p');
    heading.className = cls('share-label');
    heading.textContent = this.translate(labelKey);

    const row = this.doc.createElement('div');
    row.className = cls('share-row');

    const button = this.doc.createElement('button');
    button.type = 'button';
    button.className = `${cls('button')} ${cls(buttonClass)}`;
    button.textContent = this.translate('share.copy');
    this.registry.listen(button, 'click', () => {
      void this.copy(field as HTMLInputElement | HTMLTextAreaElement);
    });

    row.append(field, button);
    wrap.append(heading, row);
    return wrap;
  }

  /**
   * クリップボードへ書く。失敗したら選択して手動コピーを案内する。
   * document.execCommand('copy') は使わない — 非推奨なうえ失敗しても例外を投げず、
   * 成功したと嘘の通知をしてしまうため。
   */
  private async copy(field: HTMLInputElement | HTMLTextAreaElement): Promise<void> {
    try {
      await this.doc.defaultView?.navigator.clipboard.writeText(field.value);
      this.announce(this.translate('share.copied'));
    } catch {
      field.select();
      this.announce(this.translate('share.copyManual'));
    }
  }
}
